import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject, BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SignalrService {
  private hubConnection!: signalR.HubConnection;
  private hubUrl = `${environment.signalRUrl}/hubs/meditrack`;

  // Injector, not HttpClient: HttpClient's interceptors lead back to AuthService, which depends on this service.
  constructor(private injector: Injector) {}

  // ── OBSERVABLES — keeps existing dashboard + face-scanner working ──
  private _isConnected = new BehaviorSubject<boolean>(false);
  isConnected$ = this._isConnected.asObservable();

  // ReplaySubject(1, 5000) → replays the last event to any new subscriber
  // within 5 seconds. Fixes race condition where component subscribes AFTER
  // the event fires during Angular router navigation.
  private _patientArrived    = new ReplaySubject<any>(1, 5000);
  patientArrived$ = this._patientArrived.asObservable();

  private _patientRegistered = new ReplaySubject<any>(1, 5000);
  patientRegistered$ = this._patientRegistered.asObservable();

  private _vitalsRecorded    = new ReplaySubject<any>(1, 5000);
  vitalsRecorded$ = this._vitalsRecorded.asObservable();

  private _resultsReady      = new ReplaySubject<any>(1, 5000);
  resultsReady$ = this._resultsReady.asObservable();

  private _criticalResult    = new ReplaySubject<any>(1, 30000); // 30s buffer for critical
  criticalResult$ = this._criticalResult.asObservable();
  private _criticalObservation = new Subject<any>();
  criticalObservation$ = this._criticalObservation.asObservable();   // TC-1049: a nurse marked a patient Critical or Deteriorating

  private _prescriptionReady = new ReplaySubject<any>(1, 5000);
  prescriptionReady$ = this._prescriptionReady.asObservable();

  private _testAwaitingPayment = new ReplaySubject<any>(1, 5000);
  testAwaitingPayment$ = this._testAwaitingPayment.asObservable();

  private _paymentConfirmed = new ReplaySubject<any>(1, 5000);
  paymentConfirmed$ = this._paymentConfirmed.asObservable();

  // These are fire-and-forget — Subject is fine
  private _visitCompleted = new Subject<any>();
  visitCompleted$ = this._visitCompleted.asObservable();

  private _testRequested = new Subject<any>();
  testRequested$ = this._testRequested.asObservable();

  private _newMessage = new Subject<any>();
  newMessage$ = this._newMessage.asObservable();

  private _messagesRead = new Subject<any>();
  messagesRead$ = this._messagesRead.asObservable();

  private _ambulanceStatusChanged = new Subject<any>();
  ambulanceStatusChanged$ = this._ambulanceStatusChanged.asObservable();

  private _dispatchAssigned = new Subject<any>();
  dispatchAssigned$ = this._dispatchAssigned.asObservable();

  private _bedStatusChanged = new Subject<any>();
  bedStatusChanged$ = this._bedStatusChanged.asObservable();

  private _dischargeApproved = new Subject<any>();
  dischargeApproved$ = this._dischargeApproved.asObservable();

  private _nursingDone = new Subject<any>();
  nursingDone$ = this._nursingDone.asObservable();

  private _billingCleared = new Subject<any>();
  billingCleared$ = this._billingCleared.asObservable();

  // Queue state change events (doctor-dashboard real-time updates)
  private _queueUpdated = new Subject<any>();
  queueUpdated$ = this._queueUpdated.asObservable();

  // Refund/cancellation state changed (lab cancellation tab)
  private _refundUpdated = new Subject<any>();
  refundUpdated$ = this._refundUpdated.asObservable();

  // Fires after SignalR auto-reconnect so components can re-join groups
  private _reconnected = new Subject<void>();
  reconnected$ = this._reconnected.asObservable();

  // ── Track connected token to detect login changes ──
  private _lastConnectedToken: string | null = null;

  // C1: true between startConnection() and stopConnection() — restartIfDown() never reconnects a signed-out tab.
  private wanted = false;
  private restarting = false;

  // ── START CONNECTION ──────────────────────
  async startConnection(): Promise<void> {
    this.wanted = true;
    // TC-5469 (live re-test 2026-11-02): the layout and the screen both start the connection as the page opens. Each built its
    // own, only the last was remembered, and sign-out stopped only that one: the other stayed open, still delivering the
    // previous person's hospital to the next person signed in on this tab. One start at a time.
    this.starting ??= this.connect().finally(() => { this.starting = null; });
    return this.starting;
  }
  private starting: Promise<void> | null = null;

  private async connect(): Promise<void> {
    const currentToken = this._getCurrentToken();
    const state = this.hubConnection?.state;

    // If already connected, or reconnecting by itself, with the SAME token, skip
    if ((state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Reconnecting)
        && this._lastConnectedToken === currentToken) {
      return;
    }

    // If there is a connection with a DIFFERENT token (e.g., after logout+re-login), stop first
    if (this.hubConnection && state !== signalR.HubConnectionState.Disconnected) {
      console.log('🔄 SignalR: Token changed, reconnecting with fresh JWT...');
      await this.hubConnection.stop();
      this._isConnected.next(false);
    }

    this.hubConnection = this.buildConnection();

    // Wire ALL event handlers BEFORE start() — prevents race condition
    this.hubConnection.on('PatientArrived',    (d) => this._patientArrived.next(d));
    this.hubConnection.on('PatientRegistered', (d) => this._patientRegistered.next(d));
    this.hubConnection.on('DashboardUpdate',   (d) => this._patientRegistered.next(d));
    this.hubConnection.on('VisitCompleted',    (d) => this._visitCompleted.next(d));
    this.hubConnection.on('VisitUpdated',      (d) => this._visitCompleted.next(d));
    this.hubConnection.on('TestRequested',     (d) => this._testRequested.next(d));
    this.hubConnection.on('ResultsReady',      (d) => this._resultsReady.next(d));
    this.hubConnection.on('CriticalResult',    (d) => this._criticalResult.next(d));
    this.hubConnection.on('CriticalObservation', (d) => this._criticalObservation.next(d));
    this.hubConnection.on('VitalsRecorded',    (d) => this._vitalsRecorded.next(d));
    this.hubConnection.on('NewMessage',              (d) => this._newMessage.next(d));
    this.hubConnection.on('MessagesRead',            (d) => this._messagesRead.next(d));
    this.hubConnection.on('PrescriptionReady',       (d) => this._prescriptionReady.next(d));
    this.hubConnection.on('TestAwaitingPayment',     (d) => this._testAwaitingPayment.next(d));
    this.hubConnection.on('PaymentConfirmed',        (d) => this._paymentConfirmed.next(d));
    this.hubConnection.on('NewPatientRegistered',    (d) => this._patientRegistered.next(d));
    this.hubConnection.on('VisitStarted',            (d) => this._patientArrived.next(d));
    this.hubConnection.on('NewBillAdded',            (d) => this._testAwaitingPayment.next(d));
    this.hubConnection.on('AmbulanceStatusChanged',  (d) => this._ambulanceStatusChanged.next(d));
    this.hubConnection.on('DispatchAssigned',         (d) => this._dispatchAssigned.next(d));
    this.hubConnection.on('BedStatusChanged',        (d) => this._bedStatusChanged.next(d));
    this.hubConnection.on('DischargeApproved',       (d) => this._dischargeApproved.next(d));
    this.hubConnection.on('NursingDone',             (d) => this._nursingDone.next(d));
    this.hubConnection.on('BillingCleared',          (d) => this._billingCleared.next(d));
    this.hubConnection.on('QueueUpdated',            (d) => this._queueUpdated.next(d));
    this.hubConnection.on('RefundUpdated',           (d) => this._refundUpdated.next(d));

    // On auto-reconnect: groups are LOST (new connectionId) — notify layout to re-join
    this.hubConnection.onreconnected(() => {
      // Only re-join if user is still logged in
      if (!this._lastConnectedToken) return;
      this._isConnected.next(true);
      this._reconnected.next();
    });
    this.hubConnection.onreconnecting(() => {
      // Stop reconnection if user has logged out
      if (!this._lastConnectedToken) {
        this.hubConnection?.stop();
        return;
      }
      this.checkSessionIfStillDown();
    });
    this.hubConnection.onclose(() => {
      this._isConnected.next(false);
      this.checkSessionIfStillDown();   // a close sent by the server skips the reconnecting state entirely
      this.keepTryingWhileWanted();
    });

    try {
      await this.hubConnection.start();
      this._lastConnectedToken = currentToken;
      this._isConnected.next(true);
      console.log('✅ SignalR connected');
    } catch (err) {
      console.error('SignalR connection error:', err);
      this._isConnected.next(false);
    }
  }

  /**
   * TC-5496: the server closes this connection once the session ends (signed out elsewhere, switched off, hospital
   * suspended), and a screen left alone makes no other request, so it went on looking signed in. A network blip reconnects
   * at once; still down a few seconds later, ask the server. The normal sign-out handling then shows the right message.
   */
  private checkSessionIfStillDown(): void {
    setTimeout(() => {
      if (!this.wanted || !navigator.onLine || this.hubConnection?.state === signalR.HubConnectionState.Connected) return;
      this.injector.get(HttpClient).get(`${environment.apiUrl}/auth/me`, { headers: { 'X-No-Toast': '1', 'X-Silent': '1' } })
        .subscribe({ error: () => {} });
    }, SignalrService.SESSION_CHECK_DELAY_MS);
  }
  private static readonly SESSION_CHECK_DELAY_MS = 3000;

  /**
   * Live re-test 2026-11-02: automatic reconnect gives up after about 40 seconds. An API restart that took longer (any
   * deployment) left an open screen with no live events, critical results included, until the next token renewal, up to
   * 10 minutes later. Keep trying for as long as the screen is signed in.
   */
  private keepTryingWhileWanted(): void {
    if (this.restartTimer || !this.wanted) return;
    this.restartTimer = setTimeout(async () => {
      this.restartTimer = null;
      try { await this.restartIfDown(); } catch { /* still down: try again below */ }
      if (this.wanted && this.hubConnection?.state !== signalR.HubConnectionState.Connected) this.keepTryingWhileWanted();
    }, SignalrService.RESTART_EVERY_MS);
  }
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly RESTART_EVERY_MS = 30_000;

  private buildConnection(): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        // Pass JWT so the [Authorize] hub accepts the connection
        accessTokenFactory: () => {
          const stored = sessionStorage.getItem('meditrack_user');
          if (stored) {
            try { return JSON.parse(stored)?.token ?? ''; }
            catch { return ''; }
          }
          return '';
        }
      })
      .withAutomaticReconnect()
      .build();
  }

  // ── STOP / DISCONNECT ─────────────────────
  async stopConnection(): Promise<void> {
    this.wanted = false;
    this._lastConnectedToken = null;
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
    await this.starting?.catch(() => {});   // a start still in flight must not leave a connection open behind the sign-out
    await this.hubConnection?.stop();
    this._isConnected.next(false);
  }

  async disconnect(): Promise<void> {
    await this.stopConnection();
  }

  /**
   * C1: called by AuthService after a successful renewal. Emitting reconnected$ makes the layout and every
   * connectAndJoin caller re-join their groups. Retries a hub start that failed for lack of a valid token (offline
   * at start-up, or a renewal that was briefly failing); without it the screen would get no real-time events
   * (CriticalResult, BedStatusChanged, QueueUpdated) for the rest of the page's life. No AuthService dependency
   * (avoids the Auth<->SignalR cycle): accessTokenFactory reads the renewed token from sessionStorage.
   */
  async restartIfDown(): Promise<void> {
    if (!this.wanted || this.restarting) return;
    const st = this.hubConnection?.state;
    if (st === signalR.HubConnectionState.Connected
        || st === signalR.HubConnectionState.Connecting
        || st === signalR.HubConnectionState.Reconnecting) return;
    this.restarting = true;
    try {
      await this.startConnection();
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) this._reconnected.next();
    } finally {
      this.restarting = false;
    }
  }

  private _getCurrentToken(): string {
    const stored = sessionStorage.getItem('meditrack_user');
    if (stored) {
      try { return JSON.parse(stored)?.token ?? ''; }
      catch { return ''; }
    }
    return '';
  }

  // ── CONNECT + JOIN (with auto-rejoin on reconnect) ───────────────
  /**
   * Connect, run `join` once, and AUTO-REJOIN by re-running `join` on every
   * reconnected$ emission. withAutomaticReconnect gives a NEW connectionId on
   * reconnect, which DROPS all server-side group memberships — a component that
   * joins only once (in startConnection().then()) silently stops receiving
   * real-time events after any network blip. This helper makes join + re-join
   * inseparable, and swallows an initial-connect rejection (logged) instead of
   * leaving it unhandled. Returns the reconnected$ Subscription for the caller to
   * tear down with its existing pattern (subs[] push / destroyRef.onDestroy).
   *
   * `join` is a caller-supplied closure (it resolves hospitalId/staffId from the
   * component's AuthService + calls the relevant join* helpers) — keeping this
   * root service free of an AuthService dependency (avoids the Auth↔SignalR cycle).
   */
  connectAndJoin(join: () => void): Subscription {
    this.startConnection()
      .then(() => join())
      .catch((err) => console.error('SignalR connect/join failed:', err));
    return this.reconnected$.subscribe(() => {
      try { join(); } catch (err) { console.error('SignalR rejoin failed:', err); }
    });
  }

  // ── JOIN GROUPS ───────────────────────────
  async joinGroup(groupName: string): Promise<void> {
    await this.safeInvoke('JoinHospital', groupName);
  }

  async leaveGroup(groupName: string): Promise<void> {
    await this.safeInvoke('LeaveHospital', groupName);
  }

  async joinHospital(hospitalId: string): Promise<void> {
    await this.safeInvoke('JoinHospital', hospitalId);
  }

  async joinDepartment(hospitalId: string, department: string): Promise<void> {
    await this.safeInvoke('JoinDepartment', hospitalId, department);
  }

  async joinDoctorGroup(hospitalId: string, staffId: string): Promise<void> {
    await this.safeInvoke('JoinDoctorGroup', hospitalId, staffId);
  }

  async joinNurseGroup(hospitalId: string): Promise<void> {
    await this.safeInvoke('JoinNurseGroup', hospitalId);
  }

  async joinPersonalGroup(hospitalId: string, staffId: string): Promise<void> {
    console.log(`🔑 Joining personal group: hospital-${hospitalId}-staff-${staffId}`);
    await this.safeInvoke('JoinPersonalGroup', hospitalId, staffId);
  }

  // ── Circuit breaker ──────────────────────────────────────────────
  // If a hub method fails 2 times in a row we stop calling it for 60s.
  // Background: a server-side bug (BUG-3 in our case) caused JoinDepartment
  // to throw on every retry. SignalR's auto-reconnect plus the retry inside
  // each component's ngOnInit produced a 500-call/sec storm that froze the
  // browser renderer. The breaker bounds the blast radius — if the server
  // can't accept the call, we degrade to "no real-time" rather than DDoS
  // ourselves.
  private _breaker = new Map<string, { fails: number; openUntil: number }>();
  private readonly BREAKER_THRESHOLD = 2;     // fails before tripping
  private readonly BREAKER_COOLDOWN_MS = 60_000;

  private async safeInvoke(method: string, ...args: any[]): Promise<void> {
    const key = `${method}|${args.join('|')}`;
    const state = this._breaker.get(key);
    if (state && state.openUntil > Date.now()) {
      // Breaker open — silently skip. Status returns to closed when openUntil expires.
      return;
    }

    try {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
        console.log(`📡 SignalR invoke: ${method}(${args.join(', ')})`);
        await this.hubConnection.invoke(method, ...args);
        // Success: reset the breaker
        if (state) this._breaker.delete(key);
      }
    } catch (err) {
      const fails = (state?.fails ?? 0) + 1;
      if (fails >= this.BREAKER_THRESHOLD) {
        this._breaker.set(key, { fails, openUntil: Date.now() + this.BREAKER_COOLDOWN_MS });
        console.warn(`SignalR breaker OPEN for ${method} — pausing for ${this.BREAKER_COOLDOWN_MS / 1000}s`, err);
      } else {
        this._breaker.set(key, { fails, openUntil: 0 });
        console.error(`SignalR invoke '${method}' failed (${fails}/${this.BREAKER_THRESHOLD}):`, err);
      }
    }
  }

  // ── CALLBACK STYLE (for layout component) ─
  onPatientArrived(callback: (data: any) => void): void {
    this._patientArrived.subscribe(callback);
  }

  onDashboardUpdate(callback: (data: any) => void): void {
    this._patientRegistered.subscribe(callback);
  }

  onTestRequested(callback: (data: any) => void): void {
    this._testRequested.subscribe(callback);
  }

  onResultsReady(callback: (data: any) => void): void {
    this._resultsReady.subscribe(callback);
  }

  onPrescriptionReady(callback: (data: any) => void): void {
    this._prescriptionReady.subscribe(callback);
  }

  onVisitUpdated(callback: (data: any) => void): void {
    this._visitCompleted.subscribe(callback);
  }

  off(eventName: string): void {
    this.hubConnection?.off(eventName);
  }

  get isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }
}
